import axios from "axios";
import { setToken } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Sliding session: whenever the backend returns a refreshed token, persist it.
api.interceptors.response.use(
  (response) => {
    const refreshed = response.headers?.["x-refresh-token"];
    if (refreshed) setToken(refreshed);
    return response;
  },
  (error) => Promise.reject(error)
);

// ---- Public funnel ----
export const getConfig = () => api.get("/config/options").then((r) => r.data);
export const getPricingSummary = () =>
  api.get("/pricing/summary").then((r) => r.data);
export const getLandingContent = () =>
  api.get("/landing/content").then((r) => r.data);
export const createPreview = (answers, tb) =>
  api.post("/assessment/preview", answers, { params: tb ? { tb } : {} }).then((r) => r.data);
export const captureLead = (payload, tb) =>
  api.post("/lead", payload, { params: tb ? { tb } : {} }).then((r) => r.data);
export const getBlueprint = (id) =>
  api.get(`/blueprint/${id}`).then((r) => r.data);
export const downloadBlueprintPdf = (id, token) =>
  api.get(`/blueprint/${id}/pdf`, { params: { token }, responseType: "blob" });
export const requestPdfCopy = (id) =>
  api.post(`/blueprint/${id}/request-copy`).then((r) => r.data);

// ---- Admin ----
export const adminLogin = (email, password) =>
  api.post("/admin/login", { email, password }).then((r) => r.data);

const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

export const adminMe = (token) =>
  api.get("/admin/me", authHeaders(token)).then((r) => r.data);
export const adminLogout = (token) =>
  api.post("/admin/logout", {}, authHeaders(token)).then((r) => r.data);
export const adminRoles = (token) =>
  api.get("/admin/roles", authHeaders(token)).then((r) => r.data);

// ---- Admin User Management ----
export const listUsers = (token, params = {}) =>
  api.get("/admin/users", { ...authHeaders(token), params }).then((r) => r.data);
export const createUser = (token, body) =>
  api.post("/admin/users", body, authHeaders(token)).then((r) => r.data);
export const getUserDetail = (token, id) =>
  api.get(`/admin/users/${id}`, authHeaders(token)).then((r) => r.data);
export const updateUser = (token, id, body) =>
  api.patch(`/admin/users/${id}`, body, authHeaders(token)).then((r) => r.data);
export const disableUser = (token, id) =>
  api.post(`/admin/users/${id}/disable`, {}, authHeaders(token)).then((r) => r.data);
export const enableUser = (token, id) =>
  api.post(`/admin/users/${id}/enable`, {}, authHeaders(token)).then((r) => r.data);
export const resendInvite = (token, id) =>
  api.post(`/admin/users/${id}/resend-invite`, {}, authHeaders(token)).then((r) => r.data);
export const adminResetUserPassword = (token, id) =>
  api.post(`/admin/users/${id}/reset-password`, {}, authHeaders(token)).then((r) => r.data);
export const listAudit = (token, limit = 100) =>
  api.get("/admin/audit", { ...authHeaders(token), params: { limit } }).then((r) => r.data);

// ---- Public invite / reset ----
export const validateCredential = (token) =>
  api.get("/auth/credential", { params: { token } }).then((r) => r.data);
export const setPassword = (token, password) =>
  api.post("/auth/set-password", { token, password }).then((r) => r.data);
export const forgotPassword = (email) =>
  api.post("/auth/forgot-password", { email }).then((r) => r.data);

// ---- Candidate Talent Module ----
export const candidateApply = (formData) =>
  api.post("/candidates/apply", formData).then((r) => r.data);
export const candidateStats = (token) =>
  api.get("/candidates/stats", authHeaders(token)).then((r) => r.data);
export const listCandidates = (token, params = {}) =>
  api.get("/candidates", { ...authHeaders(token), params }).then((r) => r.data);
export const talentSearch = (token, body) =>
  api.post("/candidates/talent-search", body, authHeaders(token)).then((r) => r.data);
export const getCandidate = (token, id) =>
  api.get(`/candidates/${id}`, authHeaders(token)).then((r) => r.data);
export const updateCandidate = (token, id, body) =>
  api.patch(`/candidates/${id}`, body, authHeaders(token)).then((r) => r.data);
export const addCandidateNote = (token, id, text) =>
  api.post(`/candidates/${id}/notes`, { text }, authHeaders(token)).then((r) => r.data);
export const deleteCandidate = (token, id) =>
  api.delete(`/candidates/${id}`, authHeaders(token)).then((r) => r.data);
export const candidateIntakeParse = (token, file) => {
  const fd = new FormData();
  fd.append("resume", file);
  return api.post("/candidates/intake/parse", fd, authHeaders(token)).then((r) => r.data);
};
export const createCandidate = (token, body) =>
  api.post("/candidates", body, authHeaders(token)).then((r) => r.data);
export const candidateMatches = (token, id) =>
  api.get(`/candidates/${id}/matches`, authHeaders(token)).then((r) => r.data);
export const leadRecommendedCandidates = (token, submissionId) =>
  api.get(`/admin/submissions/${submissionId}/candidates`, authHeaders(token)).then((r) => r.data);
export const candidateResumeUrl = (id, download = false) =>
  `${API}/candidates/${id}/resume${download ? "?download=1" : ""}`;
export const getCandidateResumeBlob = (token, id) =>
  api.get(`/candidates/${id}/resume`, { ...authHeaders(token), responseType: "blob" });

export const adminStats = (token) =>
  api.get("/admin/stats", authHeaders(token)).then((r) => r.data);
export const adminSubmissions = (token, params = {}) =>
  api.get("/admin/submissions", { ...authHeaders(token), params }).then((r) => r.data);
export const adminSubmissionDetail = (token, id) =>
  api.get(`/admin/submissions/${id}`, authHeaders(token)).then((r) => r.data);
export const adminUpdateSubmission = (token, id, body) =>
  api.patch(`/admin/submissions/${id}`, body, authHeaders(token)).then((r) => r.data);
export const adminDownloadPdf = (token, id) =>
  api.get(`/admin/submissions/${id}/pdf`, { ...authHeaders(token), responseType: "blob" });
export const adminResendNotification = (token, id) =>
  api.post(`/admin/submissions/${id}/resend-notification`, {}, authHeaders(token)).then((r) => r.data);
export const adminRegeneratePdf = (token, id) =>
  api.post(`/admin/submissions/${id}/regenerate-pdf`, {}, authHeaders(token)).then((r) => r.data);
export const adminDeleteSubmission = (token, id) =>
  api.delete(`/admin/submissions/${id}`, authHeaders(token)).then((r) => r.data);
export const adminBulkDeleteSubmissions = (token, ids) =>
  api.post(`/admin/submissions/bulk-delete`, { ids }, authHeaders(token)).then((r) => r.data);
export const adminExportLeads = (token, body) =>
  api.post(`/admin/submissions/export`, body, { ...authHeaders(token), responseType: "blob" });

// ---- Outbound CRM ----
export const crmMeta = (token) =>
  api.get("/crm/meta", authHeaders(token)).then((r) => r.data);
export const campaignsList = (token) =>
  api.get("/crm/campaigns", authHeaders(token)).then((r) => r.data);
export const campaignCreate = (token, body) =>
  api.post("/crm/campaigns", body, authHeaders(token)).then((r) => r.data);
export const campaignUpdate = (token, id, body) =>
  api.patch(`/crm/campaigns/${id}`, body, authHeaders(token)).then((r) => r.data);
export const campaignDelete = (token, id) =>
  api.delete(`/crm/campaigns/${id}`, authHeaders(token)).then((r) => r.data);
export const crmAssignees = (token) =>
  api.get("/crm/assignees", authHeaders(token)).then((r) => r.data);
export const crmListProspects = (token, params = {}) =>
  api.get("/crm/prospects", { ...authHeaders(token), params }).then((r) => r.data);
export const crmFacets = (token, field, params = {}) =>
  api.get("/crm/facets", { ...authHeaders(token), params: { field, ...params } }).then((r) => r.data);
export const crmViewCounts = (token) =>
  api.get("/crm/views/counts", authHeaders(token)).then((r) => r.data);
export const crmStats = (token) =>
  api.get("/crm/stats", authHeaders(token)).then((r) => r.data);
export const crmGetProspect = (token, id) =>
  api.get(`/crm/prospects/${id}`, authHeaders(token)).then((r) => r.data);
export const crmCreateProspect = (token, body) =>
  api.post("/crm/prospects", body, authHeaders(token)).then((r) => r.data);
export const crmUpdateProspect = (token, id, body) =>
  api.patch(`/crm/prospects/${id}`, body, authHeaders(token)).then((r) => r.data);
export const crmLogActivity = (token, id, body) =>
  api.post(`/crm/prospects/${id}/activity`, body, authHeaders(token)).then((r) => r.data);
export const crmConvert = (token, id, body) =>
  api.post(`/crm/prospects/${id}/convert`, body, authHeaders(token)).then((r) => r.data);
export const crmCompleteAssessment = (token, id, submissionId) =>
  api.post(`/crm/prospects/${id}/assessment/complete`, { submission_id: submissionId }, authHeaders(token)).then((r) => r.data);
export const crmBulk = (token, body) =>
  api.post("/crm/prospects/bulk", body, authHeaders(token)).then((r) => r.data);
export const crmDeleteProspect = (token, id) =>
  api.delete(`/crm/prospects/${id}`, authHeaders(token)).then((r) => r.data);
export const crmImportDetect = (token, headers) =>
  api.post("/crm/import/detect", { headers }, authHeaders(token)).then((r) => r.data);
export const crmImport = (token, body) =>
  api.post("/crm/import", body, authHeaders(token)).then((r) => r.data);
export const crmExport = (token, params = {}) =>
  api.get("/crm/export", { ...authHeaders(token), params, responseType: "blob" });
export const crmDeals = (token) =>
  api.get("/crm/deals", authHeaders(token)).then((r) => r.data);
export const crmUpdateDeal = (token, id, body) =>
  api.patch(`/crm/deals/${id}`, body, authHeaders(token)).then((r) => r.data);
export const crmCampaignFunnel = (token) =>
  api.get("/crm/campaign-funnel", authHeaders(token)).then((r) => r.data);

// ---- Email Templates ----
export const emailTemplatesList = (token) =>
  api.get("/email-templates", authHeaders(token)).then((r) => r.data);
export const emailTemplateMergeFields = (token) =>
  api.get("/email-templates/merge-fields", authHeaders(token)).then((r) => r.data);
export const emailTemplateCreate = (token, body) =>
  api.post("/email-templates", body, authHeaders(token)).then((r) => r.data);
export const emailTemplateUpdate = (token, id, body) =>
  api.put(`/email-templates/${id}`, body, authHeaders(token)).then((r) => r.data);
export const emailTemplateDelete = (token, id) =>
  api.delete(`/email-templates/${id}`, authHeaders(token)).then((r) => r.data);
export const crmRenderEmail = (token, prospectId, templateId) =>
  api.post(`/crm/prospects/${prospectId}/render-email`, { template_id: templateId }, authHeaders(token)).then((r) => r.data);
export const crmTrackEmail = (token, prospectId, body) =>
  api.post(`/crm/prospects/${prospectId}/track-email`, body, authHeaders(token)).then((r) => r.data);

// ---- Global App Settings ----
export const getPricingSetting = (token) =>
  api.get("/admin/settings/pricing", authHeaders(token)).then((r) => r.data);
export const updatePricingSetting = (token, showPricing) =>
  api.put("/admin/settings/pricing", { show_pricing: showPricing }, authHeaders(token)).then((r) => r.data);
export const getLandingSetting = (token) =>
  api.get("/admin/settings/landing", authHeaders(token)).then((r) => r.data);
export const updateLandingSetting = (token, hero) =>
  api.put("/admin/settings/landing", hero, authHeaders(token)).then((r) => r.data);
export const getCompanySetting = (token) =>
  api.get("/admin/settings/company", authHeaders(token)).then((r) => r.data);
export const updateCompanySetting = (token, info) =>
  api.put("/admin/settings/company", info, authHeaders(token)).then((r) => r.data);

// ---- Email suppression (CAN-SPAM opt-outs) ----
export const listSuppressions = (token, params = {}) =>
  api.get("/admin/suppressions", { ...authHeaders(token), params }).then((r) => r.data);
export const addSuppression = (token, body) =>
  api.post("/admin/suppressions", body, authHeaders(token)).then((r) => r.data);

// ---- Public unsubscribe (no auth) ----
export const publicUnsubscribe = (token) =>
  api.post(`/unsubscribe/${token}`).then((r) => r.data);
export const unsubscribeFeedback = (token, reason) =>
  api.post(`/unsubscribe/${token}/feedback`, { reason }).then((r) => r.data);

// ---- Placement / Staffing workflow ----
export const staffingDeals = (token) =>
  api.get("/staffing/deals", authHeaders(token)).then((r) => r.data);
export const getDealPlacement = (token, dealId) =>
  api.get(`/crm/deals/${dealId}/placement`, authHeaders(token)).then((r) => r.data);
export const addPlacementCandidate = (token, dealId, body) =>
  api.post(`/crm/deals/${dealId}/placement/candidates`, body, authHeaders(token)).then((r) => r.data);
export const updatePlacementCandidate = (token, dealId, candidateId, body) =>
  api.patch(`/crm/deals/${dealId}/placement/candidates/${candidateId}`, body, authHeaders(token)).then((r) => r.data);
export const removePlacementCandidate = (token, dealId, candidateId) =>
  api.delete(`/crm/deals/${dealId}/placement/candidates/${candidateId}`, authHeaders(token)).then((r) => r.data);
export const candidateAssociations = (token, candidateId) =>
  api.get(`/candidates/${candidateId}/associations`, authHeaders(token)).then((r) => r.data);

// ---- RIMS (Role Intelligence Management System) ----
// kind: "roles" | "industries" | "tiers"
export const rimsList = (token, kind) =>
  api.get(`/admin/rims/${kind}`, authHeaders(token)).then((r) => r.data);
export const rimsUpsert = (token, kind, key, body) =>
  api.put(`/admin/rims/${kind}/${encodeURIComponent(key)}`, body, authHeaders(token)).then((r) => r.data);
export const rimsDelete = (token, kind, key) =>
  api.delete(`/admin/rims/${kind}/${encodeURIComponent(key)}`, authHeaders(token)).then((r) => r.data);

// ---- Catalog maintenance (Seed/Update Recommended Role Catalog) ----
export const catalogSeedPreview = (token) =>
  api.get("/admin/catalog/seed-preview", authHeaders(token)).then((r) => r.data);
export const catalogSeedApply = (token) =>
  api.post("/admin/catalog/seed-apply", { confirm: true }, authHeaders(token)).then((r) => r.data);
export const catalogBackups = (token) =>
  api.get("/admin/catalog/backups", authHeaders(token)).then((r) => r.data);
export const catalogBackupDownload = (token, id) =>
  api.get(`/admin/catalog/backups/${id}/download`, { ...authHeaders(token), responseType: "blob" });
export const rateIncreasePreview = (token) =>
  api.get("/admin/catalog/rate-increase-preview", authHeaders(token)).then((r) => r.data);
export const rateIncreaseApply = (token) =>
  api.post("/admin/catalog/rate-increase-apply", { confirm: true }, authHeaders(token)).then((r) => r.data);

// ---- currency helper ----
export const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString("en-US")}`;

// ---- shared investment formatter ----
// Collapses identical low/high into a single value; otherwise shows a range.
// Preserves USD formatting, thousands separators, and no decimals.
// Example: fmtInvestment(4240, 4240) -> "$4,240/month"
//          fmtInvestment(4100, 4600) -> "$4,100–$4,600/month"
export const fmtInvestment = (low, high, { suffix = "/month" } = {}) => {
  const lo = Number(low || 0);
  const hi = Number(high || 0);
  if (lo === hi) return `${fmtMoney(lo)}${suffix}`;
  return `${fmtMoney(lo)}\u2013${fmtMoney(hi)}${suffix}`;
};
